import { z } from 'zod';

import { GatheringStatusSchema, LevelSchema, RsvpSchema } from '../enums';
import { IdSchema } from './common';
import { MemberPublicSchema } from './member';

/**
 * Капитанский экран сбора (BLUEPRINT 6 `captain.*`, Stage 9 задачи 1 и 5):
 * отметка явки, завершение сбора, корректировка уровня. Доступ — `captainProcedure`.
 */

/** Как капитан отмечает участника: был / был с опозданием / не был / снять отметку. */
export const CheckinMarkSchema = z.enum(['present', 'late', 'absent', 'clear']);
export type CheckinMark = z.infer<typeof CheckinMarkSchema>;

export const CaptainCheckinInputSchema = z.object({
  gatheringId: IdSchema,
  userId: IdSchema,
  mark: CheckinMarkSchema,
});
export type CaptainCheckinInput = z.infer<typeof CaptainCheckinInputSchema>;

export const CaptainCompleteInputSchema = z.object({ gatheringId: IdSchema });
export type CaptainCompleteInput = z.infer<typeof CaptainCompleteInputSchema>;

/** Корректировка уровня участника капитаном: `squadId` нужен `captainProcedure`. */
export const CaptainAdjustLevelInputSchema = z.object({
  squadId: IdSchema,
  userId: IdSchema,
  activityId: IdSchema,
  level: LevelSchema,
});
export type CaptainAdjustLevelInput = z.infer<typeof CaptainAdjustLevelInputSchema>;

/**
 * Капитан закрывает свой сбор для разовых мест и пробных (Stage 17, ADR-35, `Gathering.singlesClosed`):
 * новые не продаются, купленные остаются.
 */
export const CaptainSinglesClosedInputSchema = z.object({
  gatheringId: IdSchema,
  closed: z.boolean(),
});
export type CaptainSinglesClosedInput = z.infer<typeof CaptainSinglesClosedInputSchema>;

/** Строка состава на капитанском экране: ответ, отметка явки и кто её поставил. */
export const CaptainRosterEntrySchema = MemberPublicSchema.extend({
  rsvp: RsvpSchema.nullable(),
  /** Отметка поставлена (капитаном или гео-чек-ином) */
  marked: z.boolean(),
  present: z.boolean(),
  late: z.boolean(),
  /** `geo` — участник отметился сам; иначе имя капитана уже не нужно клиенту */
  source: z.enum(['captain', 'geo']).nullable(),
  checkedInAt: z.string().datetime().nullable(),
  /** Скорректированный уровень по основной активности отряда (для шторки «уточнить уровень») */
  levelAdjusted: z.boolean(),
});
export type CaptainRosterEntry = z.infer<typeof CaptainRosterEntrySchema>;

/** Вид записи гостя сбора — не из состава (Stage 17, `Attendance.kind ≠ SQUAD`). */
export const GuestKindSchema = z.enum(['SUBSTITUTE', 'TRIAL', 'SINGLE']);
export type GuestKind = z.infer<typeof GuestKindSchema>;

/**
 * Гость сбора глазами капитана (раунд персон 1, C1): разовое место, замена или пробный с вручённой
 * записью явки. Как у остальных в составе — имя и фото, плюс вид записи; ни уровня, ни района.
 * Отметка капитана в явку состава и надёжность не идёт (ADR-42).
 */
export const CaptainGuestEntrySchema = z.object({
  userId: IdSchema,
  firstName: z.string(),
  photoUrl: z.string().url().nullable(),
  kind: GuestKindSchema,
  marked: z.boolean(),
  present: z.boolean(),
  late: z.boolean(),
  source: z.enum(['captain', 'geo']).nullable(),
});
export type CaptainGuestEntry = z.infer<typeof CaptainGuestEntrySchema>;

export const CaptainRosterSchema = z.object({
  gatheringId: IdSchema,
  squadId: IdSchema,
  seasonId: IdSchema,
  index: z.number().int().min(1),
  activity: z.object({ id: IdSchema, name: z.string() }),
  primaryActivityId: IdSchema,
  /** Код основной активности отряда — по нему клиент берёт якоря уровней */
  primaryActivityCode: z.string(),
  startsAt: z.string().datetime(),
  timezone: z.string(),
  status: GatheringStatusSchema,
  minAttendance: z.number().int().positive(),
  venueName: z.string().nullable(),
  members: z.array(CaptainRosterEntrySchema),
  /**
   * Гости сбора (раунд персон 1, C1): разовые, замены, пробные. Капитан отмечает их явку, но
   * `presentCount`, `unmarkedCount`, порог явки и надёжность считаются только по составу (ADR-42).
   */
  guests: z.array(CaptainGuestEntrySchema),
  /** Сколько отмечено «был» (включая опоздавших) */
  presentCount: z.number().int().nonnegative(),
  /** Сколько ещё не отмечено */
  unmarkedCount: z.number().int().nonnegative(),
  /** Сбор можно завершить: статус открыт и он уже начался */
  canComplete: z.boolean(),
  /** Уровень участника уточняется после первого проведённого сбора отряда (раунд 4, C2) */
  levelEditable: z.boolean(),
  /** Капитан закрыл сбор для разовых мест (Stage 17, `Gathering.singlesClosed`) */
  singlesClosed: z.boolean(),
  /**
   * Переключатель «Разовые места» доступен: флаг `feature.myGatherings` включён и сбор впереди.
   * С выключенным флагом клиент переключатель не показывает (`captain.setSinglesClosed` — `feature_disabled`).
   */
  canCloseSingles: z.boolean(),
  /**
   * Раунд персон 2 (C5): сколько разовых и пробных пускает сбор — доля `booking.singleShareMax` от
   * вместимости (`singleShareSeats`); подпись переключателя «до N мест»
   */
  singlesMax: z.number().int().nonnegative().default(0),
});
export type CaptainRoster = z.infer<typeof CaptainRosterSchema>;

export const GeoCheckinResultSchema = z.object({
  ok: z.boolean(),
  /** Расстояние до площадки, метры (для подсказки «слишком далеко») */
  distanceM: z.number().int().nonnegative(),
});
export type GeoCheckinResult = z.infer<typeof GeoCheckinResultSchema>;
